import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { ASC } from "const"
import {
    StoreType,
    TaskFilters,
    SortProjects,
} from "client/store"
import {
    FormGroup,
    Label,
    Input,
    SelectDropdown,
    SelectDropdownItem,
    DeadlineFilter,
    CheckButton,
    Empty,
    Button,
    CrossIcon,
    UserIcon,
} from "client/components"
import {
    SelectStatusWrapper,
    Status,
    CloseButton,
    ClearButton,
} from "./styled"

export interface SearchTaskForm {
    filters: TaskFilters
    onFilter: (filters: TaskFilters) => void
}

export const TaskFilterForm = ({
    filters,
    onFilter,
}: SearchTaskForm) => {
    const projectList = useSelector((state: StoreType) => state.project.projectList)
    const userList = useSelector((state: StoreType) => state.user.userList)
    const accountUserList = useSelector((state: StoreType) => state.accountUser.accountUserList)
    const projectUserList = useSelector((state: StoreType) => state.projectUser.projectUserList)
    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)

    const [projectIds, setProjectIds] = useState<number[]>([])

    const [name, setName] = useState(filters.name || "")
    const [projectId, setProjectId] = useState(filters.projectId)
    const [creator, setCreator] = useState(filters.creator)
    const [responsible, setResponsible] = useState(filters.responsible)
    const [highPriority, setHighPriority] = useState(filters.highPriority)

    const [deadline, setDeadline] = useState(filters.deadline)
    const [exactDate, setExactDate] = useState(filters.deadlineExactDate)
    const [customRangeStart, setCustomRangeStart] = useState(filters.deadlineCustomRangeStart)
    const [customRangeEnd, setCustomRangeEnd] = useState(filters.deadlineCustomRangeEnd)

    const [pending, setPending] = useState(filters.status ? filters.status.pending : false)
    const [inProgress, setInProgress] = useState(filters.status ? filters.status.inProgress : false)
    const [pendingReview, setPendingReview] = useState(filters.status ? filters.status.pendingReview : false)
    const [completed, setCompleted] = useState(filters.status ? filters.status.completed : false)
    const [deferred, setDeferred] = useState(filters.status ? filters.status.deferred : false)

    useEffect(() => {
        const sortedIds = SortProjects(Object.values(projectList), "name", ASC, {})
        setProjectIds(sortedIds)
    }, [projectList])

    useEffect(() => {
        setName(filters.name || "")
        setProjectId(filters.projectId)
        setCreator(filters.creator)
        setResponsible(filters.responsible)
        setDeadline(filters.deadline)
        setHighPriority(filters.highPriority)

        setPending(filters.status ? filters.status.pending : false)
        setInProgress(filters.status ? filters.status.inProgress : false)
        setPendingReview(filters.status ? filters.status.pendingReview : false)
        setCompleted(filters.status ? filters.status.completed : false)
        setDeferred(filters.status ? filters.status.deferred : false)
    }, [filters])

    const onSearch = () => {
        const newFilters: TaskFilters = {
            ...filters,
            name,
            projectId,
            creator,
            responsible,
            highPriority,

            deadline,
            deadlineExactDate: exactDate,
            deadlineCustomRangeStart: customRangeStart,
            deadlineCustomRangeEnd: customRangeEnd,

            status: {
                pending,
                inProgress,
                pendingReview,
                completed,
                deferred,
            },
        }
        onFilter(newFilters)
    }

    const showProjectStatus = project.id !== null

    const accountUsers = (accountUserList && accountUserList[userData.accountId]) || []
    const projectUsers = (project.id && projectUserList && projectUserList[project.id]) || []
    const userListIds = project.id ? projectUsers : accountUsers

    return (
        <Row>
            <Col xs={12}>
                <FormGroup>
                    <Label>{lang.name}</Label>
                    <Input type="text" value={name} onChange={(e) => setName(e.target.value)} />
                </FormGroup>
                {
                    !project.id && (
                        <FormGroup>
                            <Label>{lang.project}</Label>
                            <SelectDropdown title={
                                projectId && projectList[projectId]
                                    ? `${projectList[projectId].name} (${projectList[projectId].publicId})`
                                    : <Empty />
                            }
                            >
                                <SelectDropdownItem onClick={() => setProjectId(null)}><Empty /></SelectDropdownItem>
                                {
                                    projectIds.map((id) => (
                                        <SelectDropdownItem key={id} onClick={() => setProjectId(id)}>
                                            {`${projectList[id].name} (${projectList[id].publicId})`}
                                        </SelectDropdownItem>
                                    ))
                                }
                            </SelectDropdown>
                        </FormGroup>
                    )
                }

                <FormGroup>
                    <Label>{lang.createdBy}</Label>
                    <SelectDropdown title={
                        creator
                            ? <UserIcon user={userList[creator]} showProjectStatus={showProjectStatus} size="sm" />
                            : <Empty />
                    }
                    >
                        <SelectDropdownItem onClick={() => setCreator(null)}><Empty /></SelectDropdownItem>
                        {
                            userListIds.map((id) => (
                                <SelectDropdownItem key={id} onClick={() => setCreator(id)}>
                                    <UserIcon user={userList[id]} showProjectStatus={showProjectStatus} size="sm" />
                                </SelectDropdownItem>
                            ))
                        }
                    </SelectDropdown>
                </FormGroup>

                <FormGroup>
                    <Label>{lang.responsible}</Label>
                    <SelectDropdown title={
                        responsible
                            ? <UserIcon user={userList[responsible]} showProjectStatus={showProjectStatus} size="sm" />
                            : <Empty />
                    }
                    >
                        <SelectDropdownItem onClick={() => setResponsible(null)}><Empty /></SelectDropdownItem>
                        {
                            userListIds.map((id) => (
                                <SelectDropdownItem key={id} onClick={() => setResponsible(id)}>
                                    <UserIcon user={userList[id]} showProjectStatus={showProjectStatus} size="sm" />
                                </SelectDropdownItem>
                            ))
                        }
                    </SelectDropdown>
                </FormGroup>

                <DeadlineFilter
                    deadline={deadline}
                    exactDate={exactDate}
                    customRangeStart={customRangeStart}
                    customRangeEnd={customRangeEnd}
                    setDeadline={setDeadline}
                    setExactDate={setExactDate}
                    setCustomRangeStart={setCustomRangeStart}
                    setCustomRangeEnd={setCustomRangeEnd}
                />

                <FormGroup>
                    <CheckButton
                        padding={false}
                        title={lang.highPriority}
                        checked={highPriority}
                        onChange={() => setHighPriority(!highPriority)}
                    />
                </FormGroup>

                <FormGroup>
                    <Label>{lang.status}</Label>
                    <SelectDropdown
                        fit="true"
                        title={(
                            <SelectStatusWrapper>
                                {
                                    pending && (
                                        <Status>
                                            {lang.pending}
                                            <CloseButton onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation()
                                                setPending(false)
                                            }}
                                            >
                                                <CrossIcon />
                                            </CloseButton>
                                        </Status>
                                    )
                                }
                                {
                                    inProgress && (
                                        <Status>
                                            {lang.inProgress}
                                            <CloseButton onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation()
                                                setInProgress(false)
                                            }}
                                            >
                                                <CrossIcon />
                                            </CloseButton>
                                        </Status>
                                    )
                                }
                                {
                                    pendingReview && (
                                        <Status>
                                            {lang.pendingReview}
                                            <CloseButton onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation()
                                                setPendingReview(false)
                                            }}
                                            >
                                                <CrossIcon />
                                            </CloseButton>
                                        </Status>
                                    )
                                }
                                {
                                    completed && (
                                        <Status>
                                            {lang.completed}
                                            <CloseButton onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation()
                                                setCompleted(false)
                                            }}
                                            >
                                                <CrossIcon />
                                            </CloseButton>
                                        </Status>
                                    )
                                }
                                {
                                    deferred && (
                                        <Status>
                                            {lang.deferred}
                                            <CloseButton onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation()
                                                setDeferred(false)
                                            }}
                                            >
                                                <CrossIcon />
                                            </CloseButton>
                                        </Status>
                                    )
                                }
                                {
                                    (pending || inProgress || pendingReview || completed || deferred) && (
                                        <ClearButton onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation()
                                            setPending(false)
                                            setInProgress(false)
                                            setPendingReview(false)
                                            setCompleted(false)
                                            setDeferred(false)
                                        }}
                                        >
                                            <CrossIcon />
                                        </ClearButton>
                                    )
                                }
                            </SelectStatusWrapper>
                        )}
                    >
                        <SelectDropdownItem onClick={(e: React.MouseEvent) => { e.stopPropagation(); setPending(!pending) }}>
                            <CheckButton
                                padding={false}
                                margin={false}
                                title={lang.pending}
                                checked={pending}
                            />
                        </SelectDropdownItem>
                        <SelectDropdownItem onClick={(e: React.MouseEvent) => { e.stopPropagation(); setInProgress(!inProgress) }}>
                            <CheckButton
                                padding={false}
                                margin={false}
                                title={lang.inProgress}
                                checked={inProgress}
                            />
                        </SelectDropdownItem>
                        <SelectDropdownItem onClick={(e: React.MouseEvent) => { e.stopPropagation(); setPendingReview(!pendingReview) }}>
                            <CheckButton
                                padding={false}
                                margin={false}
                                title={lang.pendingReview}
                                checked={pendingReview}
                            />
                        </SelectDropdownItem>
                        <SelectDropdownItem onClick={(e: React.MouseEvent) => { e.stopPropagation(); setCompleted(!completed) }}>
                            <CheckButton
                                padding={false}
                                margin={false}
                                title={lang.completed}
                                checked={completed}
                            />
                        </SelectDropdownItem>
                        <SelectDropdownItem onClick={(e: React.MouseEvent) => { e.stopPropagation(); setDeferred(!deferred) }}>
                            <CheckButton
                                padding={false}
                                margin={false}
                                title={lang.deferred}
                                checked={deferred}
                            />
                        </SelectDropdownItem>
                    </SelectDropdown>
                </FormGroup>
            </Col>
            <Col xs={12}>
                <Button row onClick={onSearch}>{lang.search}</Button>
            </Col>
        </Row>
    )
}
